import { CalendarDays, Check } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import type { PortalOverviewBefore } from '../types';
import { PortalAction } from './actions';
import styles from './Overview.module.css';

const STATE_LABELS = {
  done: 'erledigt',
  customer: 'Sie sind dran',
  advisor: 'wartet auf Ihren Berater',
  open: 'offen',
} as const;

/**
 * Width of the accent overlay of the track (percent of the grid): the frames draw 38 % with the third station
 * current (14/16) and 55 % with the fourth (17) — from the left end to just past the current dot.
 */
function doneWidth(steps: PortalOverviewBefore['journey']['steps']): number {
  const current = steps.findIndex((step) => step.state !== 'done');
  if (current === -1) return 88;
  return Math.min(88, 4 + 17 * current);
}

/**
 * „Ihr Weg zum Eigentum" (Portal 14/16/17): six stations with the four dot states of A2 (done, the customer's turn
 * with the accent halo, the advisor's turn dashed navy, open) and the „Nächster Termin" block.
 */
export function Journey({
  journey,
  showStepLabel,
  preview,
}: {
  journey: PortalOverviewBefore['journey'];
  showStepLabel: boolean;
  preview?: boolean | undefined;
}) {
  const next = journey.nextAppointment;
  return (
    <section className={clsx(styles.card, styles.journey)} aria-labelledby="portal-journey-title">
      <div className={styles.journeyHead}>
        <h2 className={styles.eyebrow} id="portal-journey-title">
          Ihr Weg zum Eigentum
        </h2>
        {showStepLabel ? <span className={styles.journeyStep}>{journey.stepLabel}</span> : null}
      </div>
      <div className={styles.stationsWrap}>
        <span className={styles.track} aria-hidden="true" />
        <span
          className={styles.trackDone}
          style={{ width: `${doneWidth(journey.steps)}%` }}
          aria-hidden="true"
        />
        <ol className={styles.stations}>
          {journey.steps.map((step) => (
            <li key={step.key} className={styles.station}>
              <span
                className={clsx(
                  styles.dot,
                  step.state === 'done' && styles.dotDone,
                  step.state === 'customer' && styles.dotCustomer,
                  step.state === 'advisor' && styles.dotAdvisor,
                )}
                aria-hidden="true"
              >
                {step.state === 'done' ? <Check size={13} strokeWidth={3} /> : null}
              </span>
              <div>
                <div
                  className={clsx(
                    styles.stationLabel,
                    step.state === 'done' && styles.labelDone,
                    step.state === 'customer' && styles.labelCustomer,
                    step.state === 'advisor' && styles.labelAdvisor,
                  )}
                >
                  {step.label}
                  <span className={styles.srOnly}>, {STATE_LABELS[step.state]}</span>
                </div>
                <div
                  className={clsx(
                    styles.stationDetail,
                    step.state === 'customer' && styles.detailCustomer,
                    step.state === 'advisor' && styles.detailAdvisor,
                  )}
                >
                  {step.detail ?? 'offen'}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className={styles.nextAppointment}>
        <span
          className={clsx(styles.calendarBox, next.kind === 'booked' && styles.calendarBoxBooked)}
          aria-hidden="true"
        >
          <CalendarDays size={19} strokeWidth={1.5} />
        </span>
        <div className={styles.appointmentText}>
          <div className={styles.eyebrow}>Nächster Termin</div>
          {next.kind === 'none' ? <div className={styles.appointmentNone}>Noch keiner vereinbart</div> : null}
          {next.kind === 'booked' ? (
            <>
              <div className={styles.appointmentTitle}>
                {next.title} · {next.whenLabel}
              </div>
              <div className={styles.appointmentLine}>{next.withLine}</div>
            </>
          ) : null}
          {next.kind === 'pending' ? (
            <>
              <div className={styles.appointmentTitle}>{next.title}</div>
              <div className={styles.appointmentLine}>{next.text}</div>
            </>
          ) : null}
        </div>
        {next.kind === 'none' ? (
          <PortalAction target={{ section: 'questions', intent: 'appointment' }} preview={preview}>
            Beratungstermin buchen
          </PortalAction>
        ) : null}
        {next.kind === 'booked' && next.manageHref ? (
          <PortalAction
            target={{ href: next.manageHref }}
            preview={preview}
            variant="secondary"
            className={styles.smallSecondary}
          >
            Verschieben
          </PortalAction>
        ) : null}
      </div>
    </section>
  );
}
